// The boot screens, in plain DOM: the loading screen (staged caption, a progress bar fed only by finished work, the
// boot log as `[ OK ]` lines) and the fail screens (no WebGL2; a boot exception, with its stack folded away).
// Every player-facing string comes from the game (`BootText`). Styling is minimal and inline; the game themes it
// through the class names (`<prefix>-loading`, `<prefix>-bar`, `<prefix>-fail`…).
// The planted defects `faults.timerProgress` (the bar creeps on a timer, like a fake loader) and
// `faults.noFailScreen` (failures are swallowed) live here, beside what they break.

import { formatStepLine, type BootProgress, type StepRecord } from "./steps";

/** The player-facing strings the screens show. */
export interface BootText {
  /** Over the bar while loading (the game's name, say). */
  loadingTitle: string;
  /** The fail screen's heading. */
  failTitle: string;
  /** The message when the browser has no WebGL2. */
  noWebGL2: string;
  /** The message lead for a boot exception (the error follows). */
  bootError: string;
  /** The label of the folded stack. */
  details: string;
  /** The reload button. */
  reload: string;
}

/** Why the game can't start. */
export type FailKind = "webgl2" | "exception";

/** The loading and fail screens. */
export interface BootScreen {
  /** The loading screen's root element. */
  readonly el: HTMLElement;
  /** Planted defects: set before the first `progress` call. */
  readonly faults: { timerProgress: boolean; noFailScreen: boolean };
  /** Every value the bar showed, with the time (the runner's clock), for a test to compare with finished work. */
  readonly shown: { value: number; t: number }[];
  /** Show a progress event on the bar. */
  progress(p: BootProgress): void;
  /** Show a step starting or ending (caption and log line). */
  step(r: StepRecord): void;
  /** Every step finished: the bar shows 1. */
  complete(): void;
  /** Show a fail screen. Returns its element (null when the noFailScreen fault swallowed it). */
  fail(kind: FailKind, error?: unknown): HTMLElement | null;
  /** The visible fail screen, if any. */
  failed(): { kind: FailKind; el: HTMLElement } | null;
  /** Remove the loading screen (the fail screen stays). */
  hide(): void;
}

const css = (el: HTMLElement, s: Partial<CSSStyleDeclaration>): void => void Object.assign(el.style, s);

/** Build the loading screen into `parent` (shown at once). */
export function createBootScreen(o: { text: BootText; parent?: HTMLElement; prefix?: string; now?: () => number; doc?: Document }): BootScreen {
  const doc = o.doc ?? document;
  const parent = o.parent ?? doc.body;
  const p = o.prefix ?? "rpg-boot";
  const now = o.now ?? (() => performance.now());
  const faults = { timerProgress: false, noFailScreen: false };
  const shown: { value: number; t: number }[] = [];

  const el = doc.createElement("div");
  el.className = `${p}-loading`;
  el.setAttribute("role", "progressbar");
  el.setAttribute("aria-valuemin", "0");
  el.setAttribute("aria-valuemax", "100");
  el.setAttribute("aria-valuenow", "0");
  css(el, { position: "fixed", inset: "0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", background: "#1d2a1f", color: "#e9e4d6", font: "14px/1.4 system-ui, sans-serif", zIndex: "1000" });
  const title = doc.createElement("div");
  title.className = `${p}-title`;
  title.textContent = o.text.loadingTitle;
  css(title, { fontSize: "22px", letterSpacing: "0.04em" });
  const caption = doc.createElement("div");
  caption.className = `${p}-caption`;
  const track = doc.createElement("div");
  track.className = `${p}-track`;
  css(track, { width: "min(360px, 70vw)", height: "6px", background: "rgba(255,255,255,0.15)", borderRadius: "3px", overflow: "hidden" });
  const bar = doc.createElement("div");
  bar.className = `${p}-bar`;
  css(bar, { width: "100%", height: "100%", background: "#e9c46a", transform: "scaleX(0)", transformOrigin: "left center" });
  track.appendChild(bar);
  const log = doc.createElement("pre");
  log.className = `${p}-log`;
  css(log, { margin: "8px 0 0", font: "11px/1.5 ui-monospace, monospace", opacity: "0.7", minHeight: "6em", whiteSpace: "pre" });
  el.append(title, caption, track, log);
  parent.appendChild(el);

  const lines = new Map<string, string>();
  let failEl: { kind: FailKind; el: HTMLElement } | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let fake = 0;

  const show = (v: number): void => {
    bar.style.transform = `scaleX(${v})`;
    el.setAttribute("aria-valuenow", String(Math.round(v * 100)));
    shown.push({ value: v, t: now() });
  };

  return {
    el,
    faults,
    shown,
    progress(e) {
      if (faults.timerProgress) {
        // the planted defect: a fake loader creeping 2 % every 250 ms, ignoring the work
        timer ??= setInterval(() => show((fake = Math.min(0.98, fake + 0.02))), 250);
        return;
      }
      show(e.fraction);
    },
    step(r) {
      if (r.status === "running") caption.textContent = r.caption;
      lines.set(r.id, formatStepLine(r));
      log.textContent = [...lines.values()].join("\n");
    },
    complete() {
      if (timer) clearInterval(timer);
      timer = null;
      if (faults.timerProgress) show(1);
    },
    fail(kind, error) {
      if (timer) clearInterval(timer);
      timer = null;
      if (faults.noFailScreen) return null;
      failEl?.el.remove();
      const f = doc.createElement("div");
      f.className = `${p}-fail`;
      f.setAttribute("role", "alert");
      f.dataset.kind = kind;
      css(f, { position: "fixed", inset: "0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", padding: "16px", background: "#2a1d1d", color: "#f3e6e6", font: "15px/1.45 system-ui, sans-serif", zIndex: "1001", textAlign: "center" });
      const h = doc.createElement("h1");
      h.textContent = o.text.failTitle;
      css(h, { fontSize: "22px", margin: "0" });
      const msg = doc.createElement("p");
      msg.className = `${p}-fail-message`;
      const err = error instanceof Error ? error : error !== undefined ? new Error(String(error)) : null;
      msg.textContent = kind === "webgl2" ? o.text.noWebGL2 : `${o.text.bootError} ${err?.message ?? ""}`.trim();
      css(msg, { maxWidth: "36em", margin: "0" });
      f.append(h, msg);
      if (kind === "exception" && err) {
        const d = doc.createElement("details");
        const s = doc.createElement("summary");
        s.textContent = o.text.details;
        const pre = doc.createElement("pre");
        pre.className = `${p}-fail-stack`;
        pre.textContent = err.stack ?? err.message;
        css(pre, { textAlign: "left", maxWidth: "90vw", maxHeight: "40vh", overflow: "auto", font: "11px/1.4 ui-monospace, monospace" });
        d.append(s, pre);
        f.appendChild(d);
      }
      const b = doc.createElement("button");
      b.type = "button";
      b.textContent = o.text.reload;
      b.addEventListener("click", () => doc.defaultView?.location.reload());
      css(b, { font: "inherit", padding: "6px 16px" });
      f.appendChild(b);
      parent.appendChild(f);
      failEl = { kind, el: f };
      return f;
    },
    failed: () => failEl,
    hide() {
      if (timer) clearInterval(timer);
      timer = null;
      el.remove();
    },
  };
}
